import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["progressBar", "progressLabel", "progressMeta", "status", "submitButton"]
  static values = {
    autosaveUrl: String
  }

  connect() {
    this.saveTimer = null
    this.saving = false
    this.lastSavedAt = null
    this.updateProgressFromForm()
    this.refreshChoiceStates()
  }

  disconnect() {
    if (this.saveTimer) clearTimeout(this.saveTimer)
  }

  queue(event) {
    this.updateProgressFromForm()
    this.refreshChoiceStates()
    if (!this.hasAutosaveUrlValue) return

    if (event?.target?.type === "file") {
      this.save()
      return
    }

    if (this.saveTimer) clearTimeout(this.saveTimer)
    this.statusTarget.textContent = this.statusTarget.dataset.savingText || "Saving..."
    this.saveTimer = setTimeout(() => this.save(), 700)
  }

  previewUpload(event) {
    const input = event.target
    const file = input?.files?.[0]
    if (!file) return

    const wrapper = input.closest("[data-report-upload]")
    if (!wrapper) return

    const panel = wrapper.querySelector("[data-upload-preview-panel]")
    const image = wrapper.querySelector("[data-upload-preview-image]")
    const name = wrapper.querySelector("[data-upload-preview-name]")
    const title = wrapper.querySelector("[data-upload-preview-title]")
    const clearInput = wrapper.querySelector("[data-upload-clear-input]")
    if (!panel || !image || !name) return

    const objectUrl = URL.createObjectURL(file)
    if (image.dataset.objectUrl) URL.revokeObjectURL(image.dataset.objectUrl)

    image.src = objectUrl
    image.dataset.objectUrl = objectUrl
    name.textContent = file.name
    if (title) title.textContent = this.statusTarget.dataset.previewTitle || "Selected file"
    if (clearInput) clearInput.value = "0"
    panel.hidden = false
  }

  openUploadModal(event) {
    const image = event.currentTarget
    const wrapper = image.closest("[data-report-upload]")
    const modal = wrapper?.querySelector("[data-upload-modal]")
    const modalImage = wrapper?.querySelector("[data-upload-modal-image]")
    if (!modal || !modalImage || !image.src) return

    modalImage.src = image.src
    modal.style.display = "flex"
    document.body.style.overflow = "hidden"
  }

  closeUploadModal(event) {
    if (event && event.target !== event.currentTarget) return
    this.hideUploadModal(event.currentTarget)
  }

  closeUploadModalBtn(event) {
    this.hideUploadModal(event.currentTarget.closest("[data-upload-modal]"))
  }

  closeUploadModalKey(event) {
    if (event.key !== "Escape") return
    this.element.querySelectorAll("[data-upload-modal]").forEach((modal) => this.hideUploadModal(modal))
  }

  removeUpload(event) {
    const wrapper = event.currentTarget.closest("[data-report-upload]")
    const modal = event.currentTarget.closest("[data-upload-modal]")
    if (!wrapper) return

    const panel = wrapper.querySelector("[data-upload-preview-panel]")
    const image = wrapper.querySelector("[data-upload-preview-image]")
    const name = wrapper.querySelector("[data-upload-preview-name]")
    const title = wrapper.querySelector("[data-upload-preview-title]")
    const clearInput = wrapper.querySelector("[data-upload-clear-input]")

    wrapper.querySelectorAll('input[type="file"]').forEach((input) => { input.value = "" })
    if (image?.dataset.objectUrl) {
      URL.revokeObjectURL(image.dataset.objectUrl)
      delete image.dataset.objectUrl
    }
    if (image) image.src = ""
    if (name) name.textContent = ""
    if (title) title.textContent = this.statusTarget.dataset.savedFileTitle || "Saved file"
    if (clearInput) clearInput.value = "1"
    if (panel) panel.hidden = true

    this.hideUploadModal(modal)
    this.updateProgressFromForm()
    this.queue()
  }

  submitting() {
    if (this.saveTimer) clearTimeout(this.saveTimer)
    this.statusTarget.textContent = this.statusTarget.dataset.submittingText || "Submitting..."
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.classList.add("opacity-70", "cursor-not-allowed")
  }

  async save() {
    if (this.saving || !this.hasAutosaveUrlValue) return

    this.saving = true
    this.statusTarget.textContent = this.statusTarget.dataset.savingText || "Saving..."

    try {
      const response = await fetch(this.autosaveUrlValue, {
        method: "PATCH",
        body: new FormData(this.element),
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        credentials: "same-origin"
      })

      const data = await response.json()
      if (!response.ok) {
        throw new Error((data.errors || []).join(", "))
      }

      this.applyProgress(data.progress)
      this.statusTarget.textContent = `${this.statusTarget.dataset.savedText || "Saved"} ${data.saved_at}`
    } catch (error) {
      this.statusTarget.textContent = error.message || (this.statusTarget.dataset.errorText || "Save failed")
    } finally {
      this.saving = false
    }
  }

  updateProgressFromForm() {
    const cards = this.element.querySelectorAll("[data-report-field]")
    const total = cards.length
    let completed = 0
    let requiredTotal = 0
    let requiredCompleted = 0

    cards.forEach((card) => {
      const required = card.dataset.reportFieldRequired === "true"
      const answered = this.fieldAnswered(card)
      if (required) requiredTotal += 1
      if (answered) completed += 1
      if (required && answered) requiredCompleted += 1
    })

    this.applyProgress({
      total,
      completed,
      required_total: requiredTotal,
      required_completed: requiredCompleted,
      percentage: total === 0 ? 0 : Math.round((completed / total) * 100)
    })
  }

  applyProgress(progress) {
    if (!progress) return

    this.progressBarTarget.style.width = `${progress.percentage}%`
    this.progressLabelTarget.textContent = `${progress.completed} / ${progress.total} completed`
    this.progressMetaTarget.textContent = `${progress.required_completed} / ${progress.required_total} required answered`
  }

  refreshChoiceStates() {
    this.element.querySelectorAll('input[type="radio"]').forEach((input) => {
      const card = input.nextElementSibling
      if (!card || card.dataset.choiceCard === undefined) return

      const selectedClasses = [
        "border-primary",
        "bg-primary",
        "text-white",
        "shadow-sm"
      ]
      const idleClasses = [
        "border-slate-200",
        "bg-white",
        "text-slate-700"
      ]

      if (input.checked) {
        card.classList.remove(...idleClasses)
        card.classList.add(...selectedClasses)
      } else {
        card.classList.remove(...selectedClasses)
        card.classList.add(...idleClasses)
      }
    })
  }

  fieldAnswered(card) {
    const textInput = card.querySelector('input[type="text"], textarea')
    if (textInput) return textInput.value.trim().length > 0

    const checkedRadio = card.querySelector('input[type="radio"]:checked')
    if (checkedRadio) return true

    const fileInput = card.querySelector('input[type="file"]')
    if (fileInput?.files?.length) return true

    return card.querySelector("img") !== null
  }

  hideUploadModal(modal) {
    if (!modal) return

    const image = modal.querySelector("[data-upload-modal-image]")
    modal.style.display = "none"
    if (image) image.src = ""
    document.body.style.overflow = ""
  }
}
